import type { BeatLineViteConfig } from '../api/types';

type NativeBridgeWindow = Window & {
  BeatLineNative?: {
    appReady?: () => void;
    hideLoading?: () => void;
    openExternalAuth?: (provider: string) => void;
    logout?: () => void;
  };
  BeatLineSpotify?: {
    connect?: (clientId: string, redirectUri: string) => void;
    playTrack?: (trackUri: string, clientId: string, redirectUri: string) => void;
    pause?: () => void;
    getStatus?: () => void;
    getDebugInfo?: () => void;
  };
  BeatLineAndroidBilling?: {
    startPurchase?: (productId: string, playlistId: string, accountRef: string) => void;
    startSubscription?: (productId: string, level: string, accountRef: string) => void;
    syncOwnedPurchases?: (mappingsJson: string, accountRef: string) => void;
  };
  Capacitor?: { Plugins?: { SplashScreen?: { hide?: () => Promise<void> | void } } } | unknown;
};

export function isNativeRuntime(config?: BeatLineViteConfig): boolean {
  const w = window as NativeBridgeWindow;
  return !!(
    config?.nativeApp ||
    config?.entryContext === 'app' ||
    w.BeatLineNative ||
    w.Capacitor ||
    window.location.protocol === 'capacitor:' ||
    window.location.protocol === 'file:'
  );
}

export function markNativeAppReady(config?: BeatLineViteConfig): void {
  if (!isNativeRuntime(config)) return;
  const w = window as NativeBridgeWindow;
  const bridge = w.BeatLineNative;
  let notifiedNativeShell = false;
  try {
    if (bridge && typeof bridge.appReady === 'function') {
      bridge.appReady();
      notifiedNativeShell = true;
    }
  } catch (error) {
    if (window.BEATLINE_VITE_DEBUG) console.warn('[BeatLine Native] appReady failed', error);
  }
  try {
    if (bridge && typeof bridge.hideLoading === 'function') {
      bridge.hideLoading();
      notifiedNativeShell = true;
    }
  } catch (error) {
    if (window.BEATLINE_VITE_DEBUG) console.warn('[BeatLine Native] hideLoading failed', error);
  }

  // C40 local Capacitor path: when SplashScreen.launchAutoHide=false is used,
  // hide the native splash after the Vite shell and layout are stable.
  try {
    const cap = w.Capacitor as { Plugins?: { SplashScreen?: { hide?: () => Promise<void> | void } } } | undefined;
    const splash = cap?.Plugins?.SplashScreen;
    if (splash && typeof splash.hide === 'function') {
      void splash.hide();
      notifiedNativeShell = true;
    }
  } catch (error) {
    if (window.BEATLINE_VITE_DEBUG) console.warn('[BeatLine Native] SplashScreen.hide failed', error);
  }

  if (window.BEATLINE_VITE_DEBUG && !notifiedNativeShell) {
    console.info('[BeatLine Native] no native loading bridge detected; using Vite preloader only');
  }
}

export function installNativeRuntimeClasses(config: BeatLineViteConfig): void {
  if (!isNativeRuntime(config)) return;
  document.documentElement.classList.add('beatline-native-app');
  document.body?.classList.add('beatline-native-app');
  document.documentElement.style.setProperty('--beatline-safe-top', 'env(safe-area-inset-top, 0px)');
  document.documentElement.style.setProperty('--beatline-safe-bottom', 'env(safe-area-inset-bottom, 0px)');
}

export function installNativeBridgeDiagnostics(config: BeatLineViteConfig): void {
  if (!config.debug || !isNativeRuntime(config)) return;
  const w = window as NativeBridgeWindow;
  console.info('[BeatLine Native] bridge status', {
    nativeApp: config.nativeApp,
    entryContext: config.entryContext,
    hasBeatLineNative: !!w.BeatLineNative,
    hasSpotifyBridge: !!w.BeatLineSpotify,
    hasBillingBridge: !!w.BeatLineAndroidBilling,
    protocol: window.location.protocol,
  });
}


export function installNativeNavigationGuard(config: BeatLineViteConfig): void {
  if (!isNativeRuntime(config)) return;
  const w = window as NativeBridgeWindow & { __beatlineNativeNavigationGuard?: boolean };
  if (w.__beatlineNativeNavigationGuard) return;
  w.__beatlineNativeNavigationGuard = true;

  // Native WebView login must be allowed to navigate to Google/Facebook/Account without
  // the browser beforeunload confirmation dialog ("Navigatie bevestigen").
  const originalAddEventListener = window.addEventListener.bind(window);
  const suppressBeforeUnload = (event: Event): void => {
    try { event.stopImmediatePropagation(); } catch (_) {}
    try { delete (event as any).returnValue; } catch (_) {}
  };

  try {
    originalAddEventListener('beforeunload', suppressBeforeUnload as EventListener, { capture: true } as AddEventListenerOptions);
  } catch (_) {}

  try {
    Object.defineProperty(window, 'onbeforeunload', {
      configurable: true,
      get() { return null; },
      set() { /* Block legacy/native beforeunload handlers in app login flow. */ },
    });
  } catch (_) {
    try { (window as any).onbeforeunload = null; } catch (__) {}
  }

  try {
    (window as any).addEventListener = function(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
      if (String(type).toLowerCase() === 'beforeunload') {
        if (window.BEATLINE_VITE_DEBUG) console.info('[BeatLine Native] blocked beforeunload listener in native app context');
        return;
      }
      return originalAddEventListener(type as any, listener as any, options as any);
    };
  } catch (_) {}

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const link = target?.closest?.('a.beatline-social-login-btn, a.auth-logout') as HTMLAnchorElement | null;
    if (!link) return;
    const href = link.href || link.getAttribute('href') || '';
    if (!href || href === '#' || href.startsWith('javascript:')) return;

    try { (window as any).onbeforeunload = null; } catch (_) {}

    const provider = link.getAttribute('data-provider') || '';
    const bridge = w.BeatLineNative;
    if (link.hasAttribute('data-native-social-auth') && provider && bridge && typeof bridge.openExternalAuth === 'function') {
      event.preventDefault();
      bridge.openExternalAuth(provider);
      return;
    }

    // Use programmatic navigation after preventDefault so the WebView does not show the
    // native confirmation dialog from stale beforeunload state.
    event.preventDefault();
    window.setTimeout(() => {
      try { window.location.assign(href); }
      catch (_) { window.location.href = href; }
    }, 0);
  }, true);
}
